// batch-66 inkoop-afwijkingen (FO-497) — de pure regels van de boeteflow.
//
// Géén DB-calls, géén Fastify, géén `node:*`: alles hier is een pure functie die
// de API gebruikt en die los te testen is.
//
//  1. Statusmachine: NIEUW -> BESTUURDER_ONBEKEND / TE_BEOORDELEN -> TE_VERREKENEN
//     -> DOORGEZET_SALARIS -> (DEELS_)VERREKEND, of NIET_VERHAALD / AFGEBOEKT.
//  2. De spiegel naar het oude `verrekenStatus` (zelfde afbeelding als de trigger).
//  3. De bestuurder van een voertuig op het moment van de overtreding: nooit gokken.
//  4. Herkennen van een boeteregel op een inkoopfactuur en wat erop staat.
//  5. Hetzelfde kenmerk opnieuw (aanmaning, verhoging).
//  6. Het verrekenplan in termijnen.
//  7. Rijgedrag-drempels.
//
// Geld rekent in hele centen (integers); de DB bewaart Decimal.

import {
  BOETE_STATUS_LABELS,
  type BoeteSoort,
  type BoeteStatus,
  type BoeteVerhaalBeslissing,
  type RijgedragSignaalReden,
} from '@streep/types'

// ────────────────────────────────────────────────────────────────────────────
// 1. Statusmachine
// ────────────────────────────────────────────────────────────────────────────

/**
 * De toegestane overgangen. De drie beoordeel-statussen (NIEUW, BESTUURDER_
 * ONBEKEND, TE_BEOORDELEN) leiden met een beoordeling naar TE_VERREKENEN of
 * NIET_VERHAALD. Een bezwaar of heroverweging brengt een boete terug naar
 * TE_BEOORDELEN. De verrekening op de loonstrook beweegt tussen TE_VERREKENEN /
 * DOORGEZET_SALARIS / DEELS_VERREKEND / VERREKEND (ook terug, bij ontkoppelen).
 */
export const BOETE_TRANSITIES: Record<BoeteStatus, readonly BoeteStatus[]> = {
  NIEUW: ['BESTUURDER_ONBEKEND', 'TE_BEOORDELEN', 'TE_VERREKENEN', 'NIET_VERHAALD', 'AFGEBOEKT'],
  BESTUURDER_ONBEKEND: ['TE_BEOORDELEN', 'TE_VERREKENEN', 'NIET_VERHAALD', 'AFGEBOEKT'],
  TE_BEOORDELEN: ['BESTUURDER_ONBEKEND', 'TE_VERREKENEN', 'NIET_VERHAALD', 'AFGEBOEKT'],
  TE_VERREKENEN: [
    'BESTUURDER_ONBEKEND',
    'TE_BEOORDELEN',
    'DOORGEZET_SALARIS',
    'DEELS_VERREKEND',
    'VERREKEND',
    'NIET_VERHAALD',
    'AFGEBOEKT',
  ],
  DOORGEZET_SALARIS: ['TE_BEOORDELEN', 'TE_VERREKENEN', 'DEELS_VERREKEND', 'VERREKEND', 'AFGEBOEKT'],
  DEELS_VERREKEND: ['TE_BEOORDELEN', 'TE_VERREKENEN', 'DOORGEZET_SALARIS', 'VERREKEND', 'AFGEBOEKT'],
  VERREKEND: ['TE_BEOORDELEN', 'TE_VERREKENEN', 'DOORGEZET_SALARIS', 'DEELS_VERREKEND'],
  NIET_VERHAALD: ['TE_BEOORDELEN'],
  AFGEBOEKT: ['TE_BEOORDELEN'],
}

const MET_BESTUURDER: readonly BoeteStatus[] = ['TE_VERREKENEN', 'DOORGEZET_SALARIS', 'DEELS_VERREKEND', 'VERREKEND']
const MET_REDEN: readonly BoeteStatus[] = ['NIET_VERHAALD', 'AFGEBOEKT']

export function isBoeteOvergangToegestaan(van: BoeteStatus, naar: BoeteStatus): boolean {
  return BOETE_TRANSITIES[van].includes(naar)
}

export type BoeteOvergangFout = 'OVERGANG' | 'GEEN_BESTUURDER' | 'BEZWAAR_OPEN' | 'REDEN'

export type BoeteOvergangOordeel = { ok: true } | { ok: false; code: BoeteOvergangFout; message: string }

export interface BoeteOvergangInvoer {
  van: BoeteStatus
  naar: BoeteStatus
  /** Er is (na deze overgang) een bestuurder aan de boete gekoppeld. */
  heeftBestuurder: boolean
  /** De medewerker heeft bezwaar gemaakt dat (na deze overgang) nog niet is beoordeeld. */
  bezwaarOpen: boolean
  reden?: string | null
}

/**
 * Mag deze overgang? Naast de tabel gelden vier regels:
 *  - verhalen, doorzetten en verrekenen vragen een bestuurder;
 *  - "bestuurder onbekend" kan alleen zonder bestuurder;
 *  - een betwiste boete wordt nooit op "te verrekenen" of "doorgezet" gezet;
 *  - niet verhalen en afboeken vragen een reden.
 */
export function beoordeelBoeteOvergang(invoer: BoeteOvergangInvoer): BoeteOvergangOordeel {
  const { van, naar } = invoer
  const fout = (code: BoeteOvergangFout, message: string): BoeteOvergangOordeel => ({ ok: false, code, message })
  if (van === naar) {
    return fout('OVERGANG', `De boete staat al op ${BOETE_STATUS_LABELS[naar].toLowerCase()}.`)
  }
  if (!isBoeteOvergangToegestaan(van, naar)) {
    return fout(
      'OVERGANG',
      `Een boete met de status ${BOETE_STATUS_LABELS[van].toLowerCase()} kan niet naar ${BOETE_STATUS_LABELS[naar].toLowerCase()}.`,
    )
  }
  if (MET_BESTUURDER.includes(naar) && !invoer.heeftBestuurder) {
    return fout('GEEN_BESTUURDER', 'Wijs eerst een bestuurder toe. Zonder bestuurder valt er niets te verhalen.')
  }
  if (naar === 'BESTUURDER_ONBEKEND' && invoer.heeftBestuurder) {
    return fout('OVERGANG', 'Deze boete heeft een bestuurder; de status bestuurder onbekend past daar niet bij.')
  }
  if ((naar === 'TE_VERREKENEN' || naar === 'DOORGEZET_SALARIS') && invoer.bezwaarOpen) {
    return fout(
      'BEZWAAR_OPEN',
      'De medewerker heeft bezwaar gemaakt. Beoordeel het bezwaar eerst; een betwiste boete wordt niet verrekend of doorgezet.',
    )
  }
  if (MET_REDEN.includes(naar) && (invoer.reden ?? '').trim().length < 5) {
    return fout('REDEN', 'Geef een reden op (minstens 5 tekens).')
  }
  return { ok: true }
}

/** De status waarmee een nieuwe boete start, afhankelijk van wat er bekend is. */
export function boeteStartStatus(args: {
  bron: 'HANDMATIG' | 'INKOOPFACTUUR' | 'DOCUMENT'
  heeftBestuurder: boolean
  /** De bestuurder is direct gekozen (niet afgeleid uit een toewijzing). */
  bestuurderDirect: boolean
  heeftVoertuig: boolean
}): BoeteStatus {
  if (args.bron === 'DOCUMENT') return 'TE_BEOORDELEN'
  if (args.bron === 'INKOOPFACTUUR') {
    if (args.heeftBestuurder) return 'NIEUW'
    return args.heeftVoertuig ? 'BESTUURDER_ONBEKEND' : 'TE_BEOORDELEN'
  }
  // Handmatig (FO-033): wie een boete direct op een medewerker registreert heeft
  // hem al beoordeeld; dat gedrag blijft. Een afgeleide bestuurder is een
  // voorstel en wacht op bevestiging.
  if (!args.heeftBestuurder) return 'BESTUURDER_ONBEKEND'
  return args.bestuurderDirect ? 'TE_VERREKENEN' : 'TE_BEOORDELEN'
}

/** De status na een beoordeling (bestuurder bevestigd + verhaalbeslissing). */
export function boeteStatusNaBeoordeling(beslissing: BoeteVerhaalBeslissing): BoeteStatus {
  return beslissing === 'VERHALEN' ? 'TE_VERREKENEN' : 'NIET_VERHAALD'
}

/**
 * De status die bij de verrekenstand hoort. De stand zelf (te verrekenen / deels
 * / verrekend) komt uit de bestaande ladder van de loonstrook-controle
 * (`boeteVerrekenStand`, met centmarge); hier komt alleen het onderscheid bij
 * wanneer er niets (meer) gedekt is: een open bezwaar gaat eerst terug naar
 * beoordelen, een doorgezette boete blijft doorgezet.
 */
export function boeteStatusNaVerrekening(args: {
  stand: 'TE_VERREKENEN' | 'DEELS_VERREKEND' | 'VERREKEND'
  doorgezet: boolean
  bezwaarOpen: boolean
}): BoeteStatus {
  if (args.stand === 'VERREKEND' || args.stand === 'DEELS_VERREKEND') return args.stand
  if (args.bezwaarOpen) return 'TE_BEOORDELEN'
  return args.doorgezet ? 'DOORGEZET_SALARIS' : 'TE_VERREKENEN'
}

// ────────────────────────────────────────────────────────────────────────────
// 2. Spiegel naar het oude verrekenStatus
// ────────────────────────────────────────────────────────────────────────────

export type BoeteVerrekenStatusSpiegel = 'TE_VERREKENEN' | 'DEELS_VERREKEND' | 'VERREKEND' | 'AFGEBOEKT'

/**
 * `Boete.verrekenStatus` is alleen-lezen en volgt `status`. Exact dezelfde
 * afbeelding als de SQL-functie `b66_boete_status_naar_verreken` (migratie
 * b66_boetes); een test bewaakt dat beide gelijk blijven.
 */
export function boeteStatusNaarVerrekenStatus(status: BoeteStatus): BoeteVerrekenStatusSpiegel {
  switch (status) {
    case 'DEELS_VERREKEND':
      return 'DEELS_VERREKEND'
    case 'VERREKEND':
      return 'VERREKEND'
    case 'NIET_VERHAALD':
    case 'AFGEBOEKT':
      return 'AFGEBOEKT'
    default:
      return 'TE_VERREKENEN'
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 3. Bestuurder op het moment van de overtreding
// ────────────────────────────────────────────────────────────────────────────

export interface BoeteToewijzing {
  id: string
  employeeId: string
  startDatum: Date
  eindDatum: Date | null
}

export type BoeteBestuurderReden =
  | 'GEEN_TOEWIJZING'
  | 'EENDUIDIG'
  | 'EENDUIDIG_OP_TIJD'
  | 'WISSEL_ZONDER_TIJD'
  | 'WISSEL_ONZEKER'
  | 'NIET_OP_TIJDSTIP'

export interface BoeteBestuurderKeuze {
  employeeId: string | null
  toewijzingId: string | null
  /** Er waren toewijzingen op de dag, maar geen eenduidige bestuurder. */
  ambigu: boolean
  reden: BoeteBestuurderReden
  hint: string
  /** Alle medewerkers met een toewijzing op de dag (voor de planning-hint). */
  kandidaatEmployeeIds: string[]
}

/**
 * Een tijdstip heeft dag-precisie als het precies op het begin van de NL-dag of
 * op UTC-middernacht valt: dan weten we de dag, niet het moment van de wissel.
 */
function heeftDagPrecisie(moment: Date, dagStart: Date): boolean {
  if (moment.getTime() === dagStart.getTime()) return true
  return (
    moment.getUTCHours() === 0 &&
    moment.getUTCMinutes() === 0 &&
    moment.getUTCSeconds() === 0 &&
    moment.getUTCMilliseconds() === 0
  )
}

type Actief = 'ZEKER' | 'ONZEKER' | 'NIET'

function actiefOpMoment(t: BoeteToewijzing, dagStart: Date, dagEinde: Date, moment: Date): Actief {
  // Begin: vóór de dag = zeker begonnen. Op de dag: alleen met een echt tijdstip
  // weten we of dat vóór de overtreding was.
  let begin: Actief
  if (t.startDatum.getTime() < dagStart.getTime()) begin = 'ZEKER'
  else if (heeftDagPrecisie(t.startDatum, dagStart)) begin = 'ONZEKER'
  else begin = t.startDatum.getTime() <= moment.getTime() ? 'ZEKER' : 'NIET'

  // Einde: geen einde of na de dag = zeker nog bezig.
  let einde: Actief
  if (t.eindDatum === null || t.eindDatum.getTime() >= dagEinde.getTime()) einde = 'ZEKER'
  else if (heeftDagPrecisie(t.eindDatum, dagStart)) einde = 'ONZEKER'
  else einde = t.eindDatum.getTime() >= moment.getTime() ? 'ZEKER' : 'NIET'

  if (begin === 'NIET' || einde === 'NIET') return 'NIET'
  return begin === 'ZEKER' && einde === 'ZEKER' ? 'ZEKER' : 'ONZEKER'
}

/**
 * Wie reed dit voertuig tijdens de overtreding?
 *
 *  - Geen toewijzing op de dag: onbekend.
 *  - Eén medewerker op de dag: die medewerker. Is het tijdstip van de
 *    overtreding bekend EN begon de toewijzing aantoonbaar pas daarna (of was
 *    zij al eerder beeindigd), dan had deze medewerker het voertuig op dat
 *    moment niet: onbekend. Een toewijzing met alleen een datum (dag-precisie)
 *    houdt de bestaande dag-regel.
 *  - Wissel op de dag (meerdere medewerkers): alleen met het tijdstip van de
 *    overtreding EN toewijzingen met een echt tijdstip is de bestuurder
 *    eenduidig. Staat de wissel alleen als datum vast, dan is het onbekend.
 *
 * Er wordt nooit gegokt: twijfel = geen bestuurder, met een uitleg.
 *
 * @param dagStart begin van de NL-kalenderdag van de overtreding (UTC-instant)
 * @param dagEinde begin van de volgende NL-kalenderdag (exclusief)
 * @param moment   het tijdstip van de overtreding, of null als dat onbekend is
 */
export function kiesBoeteBestuurder(args: {
  toewijzingen: readonly BoeteToewijzing[]
  dagStart: Date
  dagEinde: Date
  moment: Date | null
}): BoeteBestuurderKeuze {
  const { dagStart, dagEinde, moment } = args
  const opDag = args.toewijzingen.filter(
    (t) =>
      t.startDatum.getTime() < dagEinde.getTime() &&
      (t.eindDatum === null || t.eindDatum.getTime() >= dagStart.getTime()),
  )
  const kandidaatEmployeeIds = [...new Set(opDag.map((t) => t.employeeId))]
  const recentste = (rijen: readonly BoeteToewijzing[]): BoeteToewijzing =>
    [...rijen].sort((a, b) => b.startDatum.getTime() - a.startDatum.getTime())[0]

  if (opDag.length === 0) {
    return {
      employeeId: null,
      toewijzingId: null,
      ambigu: false,
      reden: 'GEEN_TOEWIJZING',
      hint: 'Dit voertuig was op de dag van de overtreding aan niemand toegewezen.',
      kandidaatEmployeeIds,
    }
  }
  const nietOpTijdstip: BoeteBestuurderKeuze = {
    employeeId: null,
    toewijzingId: null,
    ambigu: true,
    reden: 'NIET_OP_TIJDSTIP',
    hint: 'Het voertuig was op het tijdstip van de overtreding aan niemand toegewezen: de toewijzing op deze dag begon later of was al beëindigd.',
    kandidaatEmployeeIds,
  }
  if (kandidaatEmployeeIds.length === 1) {
    if (moment !== null && opDag.every((t) => actiefOpMoment(t, dagStart, dagEinde, moment) === 'NIET')) {
      return nietOpTijdstip
    }
    const bron = recentste(opDag)
    return {
      employeeId: bron.employeeId,
      toewijzingId: bron.id,
      ambigu: false,
      reden: 'EENDUIDIG',
      hint: 'Afgeleid uit de voertuigtoewijzing op de dag van de overtreding.',
      kandidaatEmployeeIds,
    }
  }
  if (moment === null) {
    return {
      employeeId: null,
      toewijzingId: null,
      ambigu: true,
      reden: 'WISSEL_ZONDER_TIJD',
      hint: 'Het voertuig wisselde op deze dag van bestuurder en het tijdstip van de overtreding is onbekend.',
      kandidaatEmployeeIds,
    }
  }
  const zeker = opDag.filter((t) => actiefOpMoment(t, dagStart, dagEinde, moment) === 'ZEKER')
  const onzeker = opDag.filter((t) => actiefOpMoment(t, dagStart, dagEinde, moment) === 'ONZEKER')
  if (zeker.length === 0 && onzeker.length === 0) return nietOpTijdstip
  const zekerIds = new Set(zeker.map((t) => t.employeeId))
  const andereOnzeker = onzeker.some((t) => !zekerIds.has(t.employeeId))
  if (zekerIds.size === 1 && !andereOnzeker) {
    const bron = recentste(zeker)
    return {
      employeeId: bron.employeeId,
      toewijzingId: bron.id,
      ambigu: false,
      reden: 'EENDUIDIG_OP_TIJD',
      hint: 'Afgeleid uit de voertuigtoewijzing op het tijdstip van de overtreding.',
      kandidaatEmployeeIds,
    }
  }
  return {
    employeeId: null,
    toewijzingId: null,
    ambigu: true,
    reden: 'WISSEL_ONZEKER',
    hint: 'Het voertuig wisselde op deze dag van bestuurder en de toewijzingen leggen niet vast op welk tijdstip.',
    kandidaatEmployeeIds,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 4. Herkennen en uitlezen van een boeteregel
// ────────────────────────────────────────────────────────────────────────────

const GEEN_VERKEERSBOETE = /boete(rente|clausule|beding)|contract(uele)?\s*boete|boete\s+te\s+late\s+(betaling|levering)/i

const CJIB = /\bcjib\b|centraal\s+justitieel\s+incassobureau/i

/**
 * "Beschikking" staat ook op leges, vergunningen en subsidies. Het woord telt
 * alleen als verkeersboete samen met een verkeerswoord of een CJIB-kenmerk in
 * dezelfde omschrijving.
 */
const BESCHIKKING = /\bbeschikking(snummer)?\b/i
const VERKEER_CONTEXT = /\b(boetes?|bekeuring(en)?|overtreding(en)?|wahv|mulder)\b|verkeer|snelheid|parkeer|rood\s*licht/i

/** Trefwoorden die op zichzelf een verkeersboete aanwijzen. */
const BOETE_TREFWOORDEN_STERK: readonly RegExp[] = [
  CJIB,
  /\bfeitcode\b/i,
  /verkeers(boete|overtreding)/i,
  /\bbekeuring(en)?\b/i,
  /snelheids(overtreding|overschrijding)/i,
  /\bparkeer(boete|bon)\b/i,
  /naheffing(saanslag)?\b.*\bparke/i,
]

/** Ruimer: alleen voor een lijst die een mens bevestigt (de eenmalige voorstellen). */
const BOETE_TREFWOORDEN_RUIM: readonly RegExp[] = [/\bboetes?\b/i, /\bnaheffing(saanslag)?\b/i, /overtreding/i, BESCHIKKING]

/**
 * Lijkt deze factuurregel een verkeersboete? De afzender CJIB of een sterk
 * trefwoord (feitcode, verkeersovertreding, bekeuring) telt altijd; "beschikking"
 * alleen samen met een verkeerswoord of een CJIB-kenmerk (het woord staat ook op
 * leges en subsidies).
 * Met `ruim` tellen ook het losse woord "boete", "naheffing" en "overtreding":
 * dat is voor de eenmalige voorstellenlijst die een mens bevestigt, niet voor de
 * automatische registratie. Contractuele boetes en boeterente vallen altijd af.
 */
export function isBoeteRegelTekst(omschrijving: string, leverancierNaam?: string | null, ruim = false): boolean {
  const tekst = omschrijving ?? ''
  if (leverancierNaam && CJIB.test(leverancierNaam)) return true
  if (GEEN_VERKEERSBOETE.test(tekst)) return false
  if (BOETE_TREFWOORDEN_STERK.some((re) => re.test(tekst))) return true
  if (BESCHIKKING.test(tekst) && (VERKEER_CONTEXT.test(tekst) || boeteKenmerkUitTekst(tekst) !== null)) return true
  return ruim && BOETE_TREFWOORDEN_RUIM.some((re) => re.test(tekst))
}

/** Een CJIB-kenmerk in vrije tekst (zestien cijfers, eventueel in groepen van vier), of null. */
export function boeteKenmerkUitTekst(tekst: string): string | null {
  const m = /(?<!\d)(\d{4}[ -]?\d{4}[ -]?\d{4}[ -]?\d{4})(?!\d)/.exec(tekst ?? '')
  return m ? m[1] : null
}

/** Een feitcode in vrije tekst ("feitcode VM012", "feitcode: R 602"), of null. */
export function boeteFeitcodeUitTekst(tekst: string): string | null {
  const m = /feitcode\s*:?\s*([A-Za-z]{1,2}\s?\d{2,4}[a-z]?)(?![a-z])/i.exec(tekst ?? '')
  return m ? m[1].replace(/\s+/g, '').toUpperCase() : null
}

/** De soort uit de feitcode (waar die eenduidig is) of uit de omschrijving. */
export function boeteSoortUitTekst(omschrijving: string, feitcode?: string | null): BoeteSoort {
  const code = (feitcode ?? '').replace(/\s+/g, '').toUpperCase()
  // CJIB-feitcodes: V... = snelheid, R602 = rood licht, R545 = telefoon vasthouden.
  if (/^V[A-Z]?\d/.test(code) || /^V[A-Z]{1,2}$/.test(code)) return 'SNELHEID'
  if (/^R602/.test(code)) return 'ROOD_LICHT'
  if (/^R545/.test(code)) return 'TELEFOON_GEBRUIK'
  const t = (omschrijving ?? '').toLowerCase()
  if (/rood\s*(verkeers)?licht|roodlicht|door\s+rood/.test(t)) return 'ROOD_LICHT'
  if (/telefoon|mobiel\s+elektronisch|handheld|vasthouden\s+van/.test(t)) return 'TELEFOON_GEBRUIK'
  if (/milieuzone/.test(t)) return 'MILIEUZONE'
  if (/snelheid|te\s+hard|km\/?\s?[uh]\b/.test(t)) return 'SNELHEID'
  if (/parke(er|ren)|naheffing/.test(t)) return 'PARKEREN'
  return 'OVERIG'
}

/** "overschrijding met 23 km/u", "34 km/h te hard": de overschrijding in km/u, of null. */
export function overschrijdingUitTekst(omschrijving: string): number | null {
  const t = omschrijving ?? ''
  const m =
    /overschr\w*[^0-9]{0,30}(\d{1,3})\s*km/i.exec(t) ??
    /(\d{1,3})\s*km\s*\/?\s*(?:u|h|uur)\b[^.]{0,20}te\s+hard/i.exec(t) ??
    /te\s+hard[^0-9]{0,20}(\d{1,3})\s*km/i.exec(t)
  if (!m) return null
  const n = Number(m[1])
  return Number.isFinite(n) && n > 0 && n <= 300 ? n : null
}

/**
 * De sleutel van de dedupe: het kenmerk in hoofdletters met alleen letters en
 * cijfers, zodat "1234 5678" en "1234-5678" dezelfde boete zijn. Te kort om een
 * boete te onderscheiden (minder dan 4 tekens) = geen sleutel.
 */
export function normaliseerBoeteKenmerk(kenmerk: string | null | undefined): string | null {
  const sleutel = (kenmerk ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '')
  return sleutel.length >= 4 ? sleutel : null
}

/** "14:35", "14.35 uur", "1435", "9:05" -> "14:35" / "09:05"; anders null. */
export function parseOvertredingTijd(raw: string | null | undefined): string | null {
  const t = (raw ?? '').trim()
  if (!t) return null
  const m = /^(\d{1,2})[:.](\d{2})(?::\d{2})?(?:\s*(?:uur|u|h))?$/i.exec(t) ?? /^(\d{2})(\d{2})$/.exec(t)
  if (!m) return null
  const uur = Number(m[1])
  const minuut = Number(m[2])
  if (uur > 23 || minuut > 59) return null
  return `${String(uur).padStart(2, '0')}:${String(minuut).padStart(2, '0')}`
}

/** De voorgestelde verhaalbeslissing: verhalen, tenzij de soort is uitgezonderd. */
export function voorgesteldeVerhaalBeslissing(
  soort: BoeteSoort,
  bedrijfDraagtSoorten: readonly BoeteSoort[],
): BoeteVerhaalBeslissing {
  return bedrijfDraagtSoorten.includes(soort) ? 'BEDRIJF_DRAAGT' : 'VERHALEN'
}

// ────────────────────────────────────────────────────────────────────────────
// 5. Hetzelfde kenmerk opnieuw
// ────────────────────────────────────────────────────────────────────────────

export type ZelfdeKenmerkSoort = 'HOGER_TOTAAL' | 'DUPLICAAT' | 'EXTRA_BEDRAG'

export interface ZelfdeKenmerkOordeel {
  soort: ZelfdeKenmerkSoort
  /** Hoeveel het boetebedrag stijgt (0 bij een duplicaat). */
  verhogingCenten: number
  reden: string
}

/**
 * Hetzelfde kenmerk komt opnieuw binnen (aanmaning of verhoging). De regel
 * wordt aan de bestaande boete gekoppeld; er ontstaat geen tweede boete.
 *
 *  - Hoger bedrag dan de boete: het document noemt het nieuwe totaal; de
 *    verhoging is het verschil.
 *  - Hetzelfde bedrag: dezelfde boete nog een keer (geen verhoging).
 *  - Lager bedrag: losse extra kosten (aanmaningskosten) bovenop de boete.
 *
 * Staat het kenmerk op een tweede regel van DEZELFDE factuur met een ander
 * bedrag (`zelfdeFactuur`), dan tellen de bedragen op, in welke volgorde de
 * regels ook staan: beide regels zijn in rekening gebracht. "Het document noemt
 * het nieuwe totaal" geldt alleen voor een LATER document (een aanmaning);
 * anders gaf "Verhoging 120" gevolgd door "Boete 240" samen 240 in plaats van 360.
 */
export function beoordeelZelfdeKenmerk(args: {
  bestaandCenten: number
  nieuwCenten: number
  zelfdeFactuur?: boolean
}): ZelfdeKenmerkOordeel {
  const { bestaandCenten, nieuwCenten } = args
  if (args.zelfdeFactuur && nieuwCenten > 0 && nieuwCenten !== bestaandCenten) {
    return {
      soort: 'EXTRA_BEDRAG',
      verhogingCenten: nieuwCenten,
      reden: 'Zelfde kenmerk staat op een tweede regel van dezelfde factuur met een ander bedrag (de bedragen tellen op).',
    }
  }
  if (nieuwCenten > bestaandCenten) {
    return {
      soort: 'HOGER_TOTAAL',
      verhogingCenten: nieuwCenten - bestaandCenten,
      reden: 'Zelfde kenmerk opnieuw ontvangen met een hoger bedrag (verhoging).',
    }
  }
  if (nieuwCenten === bestaandCenten || nieuwCenten <= 0) {
    return {
      soort: 'DUPLICAAT',
      verhogingCenten: 0,
      reden: 'Zelfde kenmerk opnieuw ontvangen met hetzelfde bedrag.',
    }
  }
  return {
    soort: 'EXTRA_BEDRAG',
    verhogingCenten: nieuwCenten,
    reden: 'Zelfde kenmerk opnieuw ontvangen met een lager bedrag (extra kosten bovenop de boete).',
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 5b. Administratiekosten op dezelfde factuur, en een boete zonder regel
// ────────────────────────────────────────────────────────────────────────────

/** Woorden die een factuurregel als administratiekosten van de leasemaatschappij herkenbaar maken. */
const ADMINISTRATIEKOSTEN_RE =
  /administratie|admin\.?\s*kosten|behandel(ings)?kosten|afhandel(ings)?kosten|verwerkingskosten|servicekosten|handling/i
/** Een verhoging of aanmaning is een opslag van het CJIB: die hoort WEL bij de boete. */
const VERHOGING_RE = /verhoging|aanmaning|aanmaan|incasso|dwangbevel/i

/** Onder dit bedrag EN dit aandeel van de boete telt een tweede regel als administratiekosten (centen, fractie). */
export const ADMINISTRATIEKOSTEN_MAX_CENTEN = 5000
export const ADMINISTRATIEKOSTEN_MAX_AANDEEL = 0.3

/**
 * Dezelfde factuur noemt een kenmerk op twee regels met een verschillend bedrag.
 * Is de KLEINSTE regel de administratiekosten van de leasemaatschappij (die niet
 * worden verhaald), of een verhoging van de boete zelf (die wel bij de boete
 * hoort)? Administratiekosten als de omschrijving dat zegt, of als het bedrag
 * klein is (hoogstens 50 euro EN hoogstens 30 procent van de boete; een
 * verhoging van het CJIB is minstens de helft). Noemt de omschrijving een
 * verhoging of aanmaning, dan is het nooit administratiekosten.
 */
export function isAdministratiekostenRegel(args: {
  /** De omschrijving van de regel met het kleinste bedrag. */
  omschrijving: string | null | undefined
  kleinsteCenten: number
  grootsteCenten: number
}): boolean {
  const tekst = args.omschrijving ?? ''
  if (VERHOGING_RE.test(tekst)) return false
  if (ADMINISTRATIEKOSTEN_RE.test(tekst)) return true
  if (args.kleinsteCenten <= 0 || args.grootsteCenten <= 0) return false
  return (
    args.kleinsteCenten <= ADMINISTRATIEKOSTEN_MAX_CENTEN &&
    args.kleinsteCenten <= args.grootsteCenten * ADMINISTRATIEKOSTEN_MAX_AANDEEL
  )
}

/** Een boete van de factuur die haar regel kwijt is (de review verving de regels en zij was niet te volgen). */
export interface WeesBoete {
  id: string
  kenmerkSleutel: string | null
  /** De plek van haar oude regel; null = de regel is bij de review met zekerheid verwijderd. */
  position: number | null
  omschrijving: string | null
  /** Het bedrag van haar eigen regel (het boetebedrag zonder de verhogingen uit andere regels). */
  regelCenten: number
}

/** Een verhoging van de factuur die haar regel kwijt is. */
export interface WeesVerhoging {
  id: string
  /** Het kenmerk van de boete waar de verhoging bij hoort. */
  boeteKenmerkSleutel: string | null
  position: number | null
}

/** Een boeteregel van de factuur die nog geen boete of verhoging heeft. */
export interface VrijeBoeteRegel {
  regelId: string
  position: number
  omschrijving: string
  bedragCenten: number
  /** Het kenmerk waarmee een wees wordt teruggezocht. */
  kenmerkSleutel: string | null
  /**
   * Het kenmerk van deze regel hoort bij een ANDERE bestaande boete (een
   * aanmaning of verhoging daarvan): de regel is dan alleen op dat kenmerk te
   * koppelen, nooit op plek of bedrag.
   */
  kenmerkVanAndereBoete: boolean
}

export type WeesKoppelReden = 'KENMERK' | 'POSITIE_TEKST' | 'POSITIE_BEDRAG' | 'UNIEK_BEDRAG' | 'ENIGE'

export interface WeesKoppeling {
  boetes: { weesId: string; regelId: string; reden: WeesKoppelReden }[]
  verhogingen: { verhogingId: string; regelId: string; reden: 'KENMERK' | 'POSITIE' }[]
}

const weesTekst = (t: string | null): string => (t ?? '').toLowerCase().split(/\s+/).filter(Boolean).join(' ')

/**
 * Koppel de boetes en verhogingen van een factuur die hun regel kwijt zijn
 * opnieuw aan een boeteregel zonder boete. Zonder deze stap levert een regel
 * waarvan de boete bij de review niet te volgen was een TWEEDE boete op.
 *
 * Elke wees en elke regel hoogstens één keer, in deze volgorde (van zeker naar
 * minder zeker):
 *
 *  1. hetzelfde kenmerk (bij meerdere regels: dezelfde plek, dan hetzelfde
 *     bedrag, dan de eerste);
 *  2. dezelfde plek en dezelfde omschrijving;
 *  3. dezelfde plek en hetzelfde bedrag (de omschrijving is bijgewerkt);
 *  4. een bedrag dat aan beide kanten uniek is (de regel is verschoven en
 *     bijgewerkt);
 *  5. er is precies één wees en precies één vrije regel over: dat is dezelfde
 *     boete (het bedrag kan dan afwijken; de aanroeper past het NIET aan en meldt
 *     het).
 *
 * Nooit bij een kenmerk-conflict (de wees en de regel noemen een verschillend
 * kenmerk). Stap 2 tot en met 5 gelden niet voor een regel waarvan het kenmerk
 * bij een andere bestaande boete hoort, en stap 3 tot en met 5 niet voor een wees
 * zonder plek: haar regel is met zekerheid verwijderd, dus een nieuwe boeteregel
 * is een nieuwe boete.
 *
 * Verhogingen: op het kenmerk van hun boete (voorkeur voor dezelfde plek), anders
 * op dezelfde plek.
 */
export function koppelWezenAanRegels(args: {
  boetes: readonly WeesBoete[]
  verhogingen: readonly WeesVerhoging[]
  regels: readonly VrijeBoeteRegel[]
}): WeesKoppeling {
  const uit: WeesKoppeling = { boetes: [], verhogingen: [] }
  const regelBezet = new Set<string>()
  const weesKlaar = new Set<string>()
  const vrij = (r: VrijeBoeteRegel): boolean => !regelBezet.has(r.regelId)
  const conflict = (kenmerk: string | null, r: VrijeBoeteRegel): boolean =>
    kenmerk !== null && r.kenmerkSleutel !== null && kenmerk !== r.kenmerkSleutel
  /** Mag deze regel op plek of bedrag aan deze wees? */
  const vaag = (w: WeesBoete, r: VrijeBoeteRegel): boolean =>
    vrij(r) && !conflict(w.kenmerkSleutel, r) && !(r.kenmerkVanAndereBoete && r.kenmerkSleutel !== w.kenmerkSleutel)
  const kies = (w: WeesBoete, r: VrijeBoeteRegel, reden: WeesKoppelReden): void => {
    uit.boetes.push({ weesId: w.id, regelId: r.regelId, reden })
    regelBezet.add(r.regelId)
    weesKlaar.add(w.id)
  }
  const open = (): WeesBoete[] => args.boetes.filter((w) => !weesKlaar.has(w.id))

  // 1. Hetzelfde kenmerk.
  for (const w of open()) {
    if (w.kenmerkSleutel === null) continue
    const kandidaten = args.regels.filter((r) => vrij(r) && r.kenmerkSleutel === w.kenmerkSleutel)
    const r =
      kandidaten.find((k) => k.position === w.position) ??
      kandidaten.find((k) => k.bedragCenten === w.regelCenten) ??
      kandidaten[0]
    if (r) kies(w, r, 'KENMERK')
  }
  // 2. Dezelfde plek en dezelfde omschrijving.
  for (const w of open()) {
    if (w.position === null || weesTekst(w.omschrijving) === '') continue
    const r = args.regels.find(
      (k) => vaag(w, k) && k.position === w.position && weesTekst(k.omschrijving) === weesTekst(w.omschrijving),
    )
    if (r) kies(w, r, 'POSITIE_TEKST')
  }
  // 3. Dezelfde plek en hetzelfde bedrag.
  for (const w of open()) {
    if (w.position === null) continue
    const r = args.regels.find((k) => vaag(w, k) && k.position === w.position && k.bedragCenten === w.regelCenten)
    if (r) kies(w, r, 'POSITIE_BEDRAG')
  }
  // 4. Een bedrag dat aan beide kanten uniek is.
  for (const w of open()) {
    if (w.position === null) continue
    const kandidaten = args.regels.filter((k) => vaag(w, k) && k.bedragCenten === w.regelCenten)
    const concurrenten = open().filter((x) => x.position !== null && x.regelCenten === w.regelCenten)
    if (kandidaten.length === 1 && concurrenten.length === 1) kies(w, kandidaten[0]!, 'UNIEK_BEDRAG')
  }
  // 5. Precies één wees en precies één vrije regel over.
  const rest = open().filter((w) => w.position !== null)
  const over = args.regels.filter(vrij)
  if (rest.length === 1 && over.length === 1 && vaag(rest[0]!, over[0]!)) kies(rest[0]!, over[0]!, 'ENIGE')

  // Verhogingen: het kenmerk van hun boete, anders dezelfde plek.
  const verhogingKlaar = new Set<string>()
  for (const v of args.verhogingen) {
    if (v.boeteKenmerkSleutel === null) continue
    const kandidaten = args.regels.filter((r) => vrij(r) && r.kenmerkSleutel === v.boeteKenmerkSleutel)
    const r = kandidaten.find((k) => k.position === v.position) ?? kandidaten[0]
    if (!r) continue
    uit.verhogingen.push({ verhogingId: v.id, regelId: r.regelId, reden: 'KENMERK' })
    regelBezet.add(r.regelId)
    verhogingKlaar.add(v.id)
  }
  for (const v of args.verhogingen) {
    if (verhogingKlaar.has(v.id) || v.position === null) continue
    const r = args.regels.find(
      (k) =>
        vrij(k) &&
        k.position === v.position &&
        !conflict(v.boeteKenmerkSleutel, k) &&
        !(k.kenmerkVanAndereBoete && k.kenmerkSleutel !== v.boeteKenmerkSleutel),
    )
    if (!r) continue
    uit.verhogingen.push({ verhogingId: v.id, regelId: r.regelId, reden: 'POSITIE' })
    regelBezet.add(r.regelId)
    verhogingKlaar.add(v.id)
  }
  return uit
}

// ────────────────────────────────────────────────────────────────────────────
// 6. Verrekenperiode en verrekenplan
// ────────────────────────────────────────────────────────────────────────────

const PERIODE_RE = /^(\d{4})-(0[1-9]|1[0-2])$/

/** "2026-12" -> "2027-01". */
export function volgendeBoetePeriode(periode: string): string {
  const m = PERIODE_RE.exec(periode)
  if (!m) throw new Error(`Ongeldige periode: ${periode}`)
  const jaar = Number(m[1])
  const maand = Number(m[2])
  return maand === 12 ? `${jaar + 1}-01` : `${jaar}-${String(maand + 1).padStart(2, '0')}`
}

const MAANDEN = [
  'januari',
  'februari',
  'maart',
  'april',
  'mei',
  'juni',
  'juli',
  'augustus',
  'september',
  'oktober',
  'november',
  'december',
]

/** "2026-10" -> "oktober 2026". */
export function boetePeriodeLabel(periode: string): string {
  const m = PERIODE_RE.exec(periode)
  if (!m) return periode
  return `${MAANDEN[Number(m[2]) - 1]} ${m[1]}`
}

/** Het begin van de verrekenperiode als UTC-instant (eerste dag van de maand). */
export function boetePeriodeStart(periode: string): Date | null {
  const m = PERIODE_RE.exec(periode)
  if (!m) return null
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1))
}

export interface VerrekenplanBoete {
  id: string
  /** Wat er nog openstaat. */
  openCenten: number
  /** Overtredingsdatum (jjjj-mm-dd); de oudste boete wordt het eerst verrekend. */
  datum: string
}

export interface VerrekenTermijn {
  periode: string
  bedragCenten: number
}

export interface Verrekenplan {
  perBoete: Record<string, VerrekenTermijn[]>
  perMaand: VerrekenTermijn[]
  /** Het plan loopt over meer dan één maand. */
  inTermijnen: boolean
}

/** Hoogste aantal maanden dat een plan mag beslaan (vangnet tegen een te laag termijnbedrag). */
export const VERREKENPLAN_MAX_MAANDEN = 60

/**
 * Verdeelt de openstaande boetes van één medewerker over de maanden: per maand
 * hoogstens `maxPerMaandCenten`, de rest schuift door. De oudste boete eerst.
 * Geen grens (0 of minder) = alles in de startperiode. Na 60 maanden komt de
 * rest in de laatste maand, zodat er nooit een bedrag verdwijnt.
 *
 * `bezetPerPeriode` = wat er in een maand al wordt ingehouden uit eerder
 * doorgezette boetes (hun lopende plan). De grens geldt per medewerker per
 * maand, dus een nieuwe boete krijgt in zo'n maand alleen de ruimte die over is.
 */
export function maakVerrekenplan(args: {
  boetes: readonly VerrekenplanBoete[]
  maxPerMaandCenten: number
  startPeriode: string
  bezetPerPeriode?: Readonly<Record<string, number>>
}): Verrekenplan {
  const perBoete: Record<string, VerrekenTermijn[]> = {}
  const maandBedragen = new Map<string, number>()
  const gesorteerd = [...args.boetes]
    .filter((b) => b.openCenten > 0)
    .sort((a, b) => (a.datum === b.datum ? a.id.localeCompare(b.id) : a.datum.localeCompare(b.datum)))
  const grens = args.maxPerMaandCenten > 0 ? args.maxPerMaandCenten : Number.MAX_SAFE_INTEGER
  const ruimteIn = (p: string): number =>
    grens === Number.MAX_SAFE_INTEGER ? grens : Math.max(0, grens - Math.max(0, args.bezetPerPeriode?.[p] ?? 0))

  let periode = args.startPeriode
  let ruimte = ruimteIn(periode)
  let maanden = 1
  for (const boete of gesorteerd) {
    let rest = boete.openCenten
    const termijnen: VerrekenTermijn[] = []
    while (rest > 0) {
      if (ruimte <= 0) {
        if (maanden >= VERREKENPLAN_MAX_MAANDEN) {
          ruimte = Number.MAX_SAFE_INTEGER
        } else {
          periode = volgendeBoetePeriode(periode)
          ruimte = ruimteIn(periode)
          maanden += 1
        }
      }
      // Een maand die al vol zit (ruimte 0) wordt overgeslagen in de lus hierboven.
      if (ruimte <= 0) continue
      const deel = Math.min(rest, ruimte)
      termijnen.push({ periode, bedragCenten: deel })
      maandBedragen.set(periode, (maandBedragen.get(periode) ?? 0) + deel)
      rest -= deel
      ruimte -= deel
    }
    perBoete[boete.id] = termijnen
  }
  const perMaand = [...maandBedragen.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([p, bedragCenten]) => ({ periode: p, bedragCenten }))
  return { perBoete, perMaand, inTermijnen: perMaand.length > 1 }
}

/** Lees een verrekenplan uit de JSON-kolom (defensief: een vreemde vorm = geen plan). */
export function leesVerrekenplan(json: unknown): VerrekenTermijn[] {
  if (!Array.isArray(json)) return []
  return json.flatMap((t) => {
    if (!t || typeof t !== 'object' || Array.isArray(t)) return []
    const rij = t as Record<string, unknown>
    return typeof rij.periode === 'string' &&
      PERIODE_RE.test(rij.periode) &&
      typeof rij.bedragCenten === 'number' &&
      Number.isFinite(rij.bedragCenten)
      ? [{ periode: rij.periode, bedragCenten: Math.round(rij.bedragCenten) }]
      : []
  })
}

/** De eerste periode van een plan ("jjjj-mm"), of null zonder plan. */
export function eersteTermijnPeriode(plan: readonly VerrekenTermijn[]): string | null {
  return plan.reduce<string | null>((min, t) => (min === null || t.periode < min ? t.periode : min), null)
}

/** Wat er in DEZE periode volgens het plan wordt ingehouden (centen; 0 = niets). */
export function termijnInPeriode(plan: readonly VerrekenTermijn[], periode: string): number {
  return plan.reduce((som, t) => (t.periode === periode ? som + t.bedragCenten : som), 0)
}

/**
 * Hoeveel loopt deze boete ACHTER op het verrekenplan (centen)? Het plan dekt
 * wat er bij het doorzetten openstond; wat daarvoor al verrekend was telt dus
 * niet mee. Verwacht = de termijnen tot en met `totEnMetPeriode`; achterstand =
 * verwacht min wat er sinds het doorzetten op de loonstroken is ingehouden.
 * Een boete in termijnen die op schema ligt heeft dus geen achterstand.
 */
export function boeteAchterstandCenten(args: {
  bedragCenten: number
  verrekendCenten: number
  plan: readonly VerrekenTermijn[]
  totEnMetPeriode: string
}): number {
  const planTotaal = args.plan.reduce((som, t) => som + t.bedragCenten, 0)
  if (planTotaal <= 0) return 0
  const alVoorDoorzetten = Math.max(0, args.bedragCenten - planTotaal)
  const sindsDoorzetten = Math.max(0, args.verrekendCenten - alVoorDoorzetten)
  const verwacht = args.plan.reduce((som, t) => (t.periode <= args.totEnMetPeriode ? som + t.bedragCenten : som), 0)
  return Math.max(0, verwacht - sindsDoorzetten)
}

/**
 * De laatste verrekenperiode waarvan de loonrun gedraaid hoort te zijn: vanaf
 * de tiende van de maand is dat de vorige maand, daarvoor de maand ervoor.
 * `nlDag` = de NL-kalenderdag "jjjj-mm-dd" van vandaag.
 */
export function laatsteVerstrekenBoetePeriode(nlDag: string): string {
  const [jaar, maand, dag] = nlDag.split('-').map(Number)
  const terug = (dag ?? 1) >= 10 ? 1 : 2
  const d = new Date(Date.UTC(jaar ?? 1970, (maand ?? 1) - 1 - terug, 1))
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

/**
 * Hoort deze boete in DEZE loonrun verrekend te zijn? De overtreding ligt vóór
 * het einde van de periode, en een boete met een opgave bij de
 * salarisadministratie telt pas vanaf de EERSTE TERMIJN van haar plan: een boete
 * die achter een lopend plan van dezelfde medewerker schuift, begint later dan de
 * periode waarvoor zij is doorgezet (anders meldt de bewaking te vroeg "niet
 * verrekend").
 *
 * De OPGAVE beslist, niet de status: ook een boete waarvan al een deel is
 * verrekend hoort niet in een run van voor haar eerste termijn. Anders zoekt een
 * herdraai van zo'n oudere run alsnog een inhouding op een strook waar de opgave
 * niets over zegt. Zonder opgave is elke openstaande boete in elke run verwacht.
 */
export function boeteVerwachtInRun(
  boete: { datum: Date; doorgezetPeriode: string | null; eerstePlanPeriode?: string | null },
  periodeEinde: Date,
): boolean {
  if (boete.datum.getTime() >= periodeEinde.getTime()) return false
  const vanaf = boete.eerstePlanPeriode ?? boete.doorgezetPeriode
  if (!vanaf) return true
  const start = boetePeriodeStart(vanaf)
  return start === null || start.getTime() < periodeEinde.getTime()
}

// ─── De ingetrokken opgaven ──────────────────────────────────────────────────

/**
 * Een opgave aan de salarisadministratie die is ingetrokken (de boete is
 * heropend, betwist, verhoogd, afgeboekt of de opgave is teruggedraaid nadat zij
 * was verstuurd). Een boete kan er meerdere hebben (opgave A ingetrokken, opgave
 * B verstuurd en daarna ook ingetrokken). Elke opgave weet op wiens naam zij
 * stond: de bestuurder kan intussen zijn gewisseld, en een inhouding op de oude
 * opgave komt dan bij de VORIGE medewerker terecht.
 *
 * Een opgave blijft bij de boete staan tot de loonrun NA haar laatste termijn is
 * gecontroleerd. Tot die tijd noemt een volgende opgave haar als vervangen, en
 * wordt een inhouding die ondanks de intrekking plaatsvindt gevonden.
 */
export interface IngetrokkenBoeteOpgave {
  doorzettingId: string | null
  /** De periode waarvoor de opgave was doorgezet ("jjjj-mm"). */
  periode: string
  /** Wat er op het moment van intrekken nog volgens het plan zou worden ingehouden. */
  bedragCenten: number
  /** De termijnen die nog openstonden. */
  plan: VerrekenTermijn[]
  /** ISO-tijdstip van de intrekking. */
  op: string
  /** De medewerker op wiens naam de opgave stond (null = onbekend: een rij van voor deze regel). */
  employeeId: string | null
  employeeNaam: string | null
}

function leesEenIngetrokkenOpgave(json: unknown): IngetrokkenBoeteOpgave | null {
  if (!json || typeof json !== 'object' || Array.isArray(json)) return null
  const rij = json as Record<string, unknown>
  if (typeof rij.periode !== 'string' || !PERIODE_RE.test(rij.periode)) return null
  const plan = leesVerrekenplan(rij.plan)
  const bedrag = typeof rij.bedragCenten === 'number' && Number.isFinite(rij.bedragCenten) ? Math.round(rij.bedragCenten) : 0
  return {
    doorzettingId: typeof rij.doorzettingId === 'string' ? rij.doorzettingId : null,
    periode: rij.periode,
    bedragCenten: bedrag,
    plan,
    op: typeof rij.op === 'string' ? rij.op : '',
    employeeId: typeof rij.employeeId === 'string' && rij.employeeId ? rij.employeeId : null,
    employeeNaam: typeof rij.employeeNaam === 'string' && rij.employeeNaam ? rij.employeeNaam : null,
  }
}

/**
 * Lees de ingetrokken opgaven uit de JSON-kolom (defensief). De kolom is een
 * lijst; een los object (de vorm van voor de lijst) telt als lijst van één.
 */
export function leesIngetrokkenOpgaven(json: unknown): IngetrokkenBoeteOpgave[] {
  const rijen = Array.isArray(json) ? json : json == null ? [] : [json]
  return rijen.flatMap((r) => {
    const opgave = leesEenIngetrokkenOpgave(r)
    return opgave ? [opgave] : []
  })
}

/** De laatste periode van een plan ("jjjj-mm"), of null zonder plan. */
export function laatsteTermijnPeriode(plan: readonly VerrekenTermijn[]): string | null {
  return plan.reduce<string | null>((max, t) => (max === null || t.periode > max ? t.periode : max), null)
}

/**
 * Is de bewaking van deze ingetrokken opgave klaar? Ja zodra de loonrun van een
 * LATERE periode dan haar laatste termijn is gecontroleerd: de run van de
 * termijn zelf en de run erna (een inhouding die een maand te laat komt) zijn
 * dan allebei gezien.
 */
export function isIngetrokkenOpgaveVerlopen(opgave: IngetrokkenBoeteOpgave, gecontroleerdePeriode: string): boolean {
  const laatste = laatsteTermijnPeriode(opgave.plan) ?? opgave.periode
  return gecontroleerdePeriode > laatste
}

/** Hoe lang een ingetrokken opgave na haar laatste termijn hoogstens bij de boete blijft staan (maanden). */
export const INGETROKKEN_OPGAVE_BEWAARMAANDEN = 3

/**
 * Is deze ingetrokken opgave te oud om nog te bewaken? Ja zodra de huidige
 * periode meer dan `INGETROKKEN_OPGAVE_BEWAARMAANDEN` na haar laatste termijn
 * ligt. Het vangnet naast `isIngetrokkenOpgaveVerlopen`: die regel wacht op een
 * gecontroleerde loonrun van de medewerker, en die komt er niet als hij uit
 * dienst is of per vier weken wordt verloond.
 */
export function isIngetrokkenOpgaveVerjaard(opgave: IngetrokkenBoeteOpgave, huidigePeriode: string): boolean {
  let grens = laatsteTermijnPeriode(opgave.plan) ?? opgave.periode
  for (let i = 0; i < INGETROKKEN_OPGAVE_BEWAARMAANDEN; i += 1) grens = volgendeBoetePeriode(grens)
  return huidigePeriode > grens
}

/**
 * De bedragen waarop een ingetrokken opgave in DEZE run nog kan terugkomen
 * (centen, meest waarschijnlijke eerst), in twee groepen:
 *
 *  - `opTijd`: de opgave had volgens haar plan een termijn in deze run. De
 *    termijn zelf, die termijn plus wat er eerder had moeten gebeuren, en het
 *    hele bedrag in één keer;
 *  - `laat`: wat er VOOR deze run al had moeten gebeuren (een maand te laat
 *    ingehouden). Zegt het plan niets over deze run, dan is dat alles wat de
 *    opgave nog kan opleveren.
 *
 * De bewaking vergeeft een regel eerst aan de opgaven die op tijd zijn. Anders
 * gaat een inhouding naar een oudere opgave ("een maand te laat") terwijl zij
 * precies de termijn van een latere opgave is.
 */
export function ingetrokkenKandidaten(
  opgave: IngetrokkenBoeteOpgave,
  runPeriode: string,
): { opTijd: number[]; laat: number[] } {
  const nu = termijnInPeriode(opgave.plan, runPeriode)
  const eerder = opgave.plan.reduce((som, t) => (t.periode < runPeriode ? som + t.bedragCenten : som), 0)
  const uniek = (lijst: readonly number[]): number[] => [...new Set(lijst.filter((c) => c > 0))]
  if (nu <= 0) return { opTijd: [], laat: uniek([eerder, opgave.bedragCenten]) }
  const opTijd = uniek([nu, nu + eerder, opgave.bedragCenten])
  return { opTijd, laat: uniek([eerder]).filter((c) => !opTijd.includes(c)) }
}

/**
 * De opgave die vervalt als een doorgezette boete wordt heropend of afgeboekt:
 * de termijnen die nog NIET zijn ingehouden. Is er sinds het doorzetten al een
 * deel verrekend, dan gaat dat van de oudste termijnen af. Geen plan of niets
 * meer open = null (er valt niets in te trekken).
 *
 * `bedragCenten` is wat er via de loonstrook te verhalen is (het boetebedrag min
 * wat buiten de loonstrook om is verhaald).
 */
export function maakIngetrokkenOpgave(args: {
  doorzettingId: string | null
  doorgezetPeriode: string | null
  plan: readonly VerrekenTermijn[]
  bedragCenten: number
  verrekendCenten: number
  op: Date
  employeeId: string | null
  employeeNaam: string | null
}): IngetrokkenBoeteOpgave | null {
  if (!args.doorgezetPeriode) return null
  const gesorteerd = [...args.plan].sort((a, b) => a.periode.localeCompare(b.periode))
  const planTotaal = gesorteerd.reduce((som, t) => som + t.bedragCenten, 0)
  const open = Math.max(0, args.bedragCenten - args.verrekendCenten)
  if (open <= 0) return null
  let alIngehouden = Math.max(0, planTotaal - open)
  const rest: VerrekenTermijn[] = []
  for (const t of gesorteerd) {
    const af = Math.min(alIngehouden, t.bedragCenten)
    alIngehouden -= af
    if (t.bedragCenten - af > 0) rest.push({ periode: t.periode, bedragCenten: t.bedragCenten - af })
  }
  // Geen plan vastgelegd: het hele openstaande bedrag hoorde bij de doorgezette periode.
  const plan = rest.length > 0 ? rest : [{ periode: args.doorgezetPeriode, bedragCenten: open }]
  return {
    doorzettingId: args.doorzettingId,
    periode: args.doorgezetPeriode,
    bedragCenten: plan.reduce((som, t) => som + t.bedragCenten, 0),
    plan,
    op: args.op.toISOString(),
    employeeId: args.employeeId,
    employeeNaam: args.employeeNaam,
  }
}

/** De JSON-vorm van de lijst ingetrokken opgaven (voor de kolom). */
export function ingetrokkenOpgavenNaarJson(opgaven: readonly IngetrokkenBoeteOpgave[]): Record<string, unknown>[] {
  return opgaven.map((o) => ({
    doorzettingId: o.doorzettingId,
    periode: o.periode,
    bedragCenten: o.bedragCenten,
    plan: o.plan.map((t) => ({ periode: t.periode, bedragCenten: t.bedragCenten })),
    op: o.op,
    employeeId: o.employeeId,
    employeeNaam: o.employeeNaam,
  }))
}

// ─── Buiten de loonstrook om verhaald ────────────────────────────────────────

/**
 * Wat er via de LOONSTROOK te verhalen is (centen): het boetebedrag min wat al
 * buiten de loonstrook om is verhaald (contant of anders). Alle verreken-
 * rekenwerk (open bedrag, plan, "volledig verrekend") gaat over dit bedrag.
 */
export function boeteLoonstrookCenten(bedragCenten: number, buitenLoonstrookCenten: number | null | undefined): number {
  return Math.max(0, bedragCenten - Math.max(0, buitenLoonstrookCenten ?? 0))
}

// ────────────────────────────────────────────────────────────────────────────
// 7. Rijgedrag
// ────────────────────────────────────────────────────────────────────────────

export interface RijgedragBoete {
  id: string
  /** Overtredingsdatum (dag-precisie). */
  datum: Date
  soort: BoeteSoort
  overschrijdingKmu: number | null
  bedragCenten: number
}

export interface RijgedragDrempels {
  kortAantal: number
  kortDagen: number
  langAantal: number
  langMaanden: number
  ernstigeSoorten: readonly BoeteSoort[]
  ernstigeSnelheidKmu: number
}

export const RIJGEDRAG_DREMPELS_STANDAARD: RijgedragDrempels = {
  kortAantal: 2,
  kortDagen: 90,
  langAantal: 3,
  langMaanden: 12,
  ernstigeSoorten: ['ROOD_LICHT', 'TELEFOON_GEBRUIK'],
  ernstigeSnelheidKmu: 30,
}

/** Een ernstige overtreding: een ernstige soort, of een snelheidsboete vanaf de grens. */
export function isErnstigeBoete(
  boete: Pick<RijgedragBoete, 'soort' | 'overschrijdingKmu'>,
  drempels: Pick<RijgedragDrempels, 'ernstigeSoorten' | 'ernstigeSnelheidKmu'>,
): boolean {
  if (drempels.ernstigeSoorten.includes(boete.soort)) return true
  return (
    boete.soort === 'SNELHEID' &&
    boete.overschrijdingKmu !== null &&
    boete.overschrijdingKmu >= drempels.ernstigeSnelheidKmu
  )
}

function maandenTerug(datum: Date, maanden: number): Date {
  const d = new Date(datum.getTime())
  const dag = d.getUTCDate()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() - maanden)
  const laatsteDag = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate()
  d.setUTCDate(Math.min(dag, laatsteDag))
  return d
}

export interface RijgedragOordeel {
  redenen: RijgedragSignaalReden[]
  /** De boetes die samen het signaal vormen (het ruimste venster dat een drempel raakt). */
  boeteIds: string[]
  aantal: number
  bedragCenten: number
}

/**
 * Raakt deze boete (de `trigger`) een rijgedrag-drempel? Een venster is een
 * reeks boetes binnen `kortDagen` dagen (of `langMaanden` maanden) van elkaar
 * waar de trigger in valt. Het venster hoeft NIET bij de trigger te eindigen:
 * wordt een oudere overtreding pas na een nieuwere bevestigd, dan vormen die
 * twee samen evengoed een reeks. Van alle vensters met de trigger telt het
 * drukste. Alleen bevestigde boetes horen in `boetes` (dat filtert de aanroeper).
 */
export function beoordeelRijgedrag(args: {
  boetes: readonly RijgedragBoete[]
  triggerId: string
  drempels: RijgedragDrempels
}): RijgedragOordeel {
  const leeg: RijgedragOordeel = { redenen: [], boeteIds: [], aantal: 0, bedragCenten: 0 }
  const trigger = args.boetes.find((b) => b.id === args.triggerId)
  if (!trigger) return leeg
  const { drempels } = args
  const t = trigger.datum.getTime()
  // Elk venster eindigt bij een boete op of na de trigger en loopt terug over
  // de termijn; het telt alleen als de trigger erin valt.
  const drukste = (vanaf: (einde: Date) => number): RijgedragBoete[] => {
    let beste: RijgedragBoete[] = []
    for (const einde of args.boetes) {
      const tot = einde.datum.getTime()
      if (tot < t) continue
      const van = vanaf(einde.datum)
      if (t <= van) continue
      const leden = args.boetes.filter((b) => b.datum.getTime() > van && b.datum.getTime() <= tot)
      if (leden.length > beste.length) beste = leden
    }
    return beste
  }
  const inKort = drukste((einde) => einde.getTime() - drempels.kortDagen * 86_400_000)
  const inLang = drukste((einde) => maandenTerug(einde, drempels.langMaanden).getTime())

  const redenen: RijgedragSignaalReden[] = []
  if (inKort.length >= drempels.kortAantal) redenen.push('KORTE_TERMIJN')
  if (inLang.length >= drempels.langAantal) redenen.push('LANGE_TERMIJN')
  if (isErnstigeBoete(trigger, drempels)) redenen.push('ERNSTIG')
  if (redenen.length === 0) return leeg

  const set = redenen.includes('LANGE_TERMIJN') ? inLang : redenen.includes('KORTE_TERMIJN') ? inKort : [trigger]
  const gesorteerd = [...set].sort((a, b) => a.datum.getTime() - b.datum.getTime())
  return {
    redenen,
    boeteIds: gesorteerd.map((b) => b.id),
    aantal: gesorteerd.length,
    bedragCenten: gesorteerd.reduce((som, b) => som + b.bedragCenten, 0),
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 8. Tekst voor de medewerker (portal)
// ────────────────────────────────────────────────────────────────────────────

/** De status in gewone taal voor de medewerker zelf (geen interne termen). */
export function mijnBoeteStatusTekst(args: {
  status: BoeteStatus
  bezwaarOpen: boolean
  doorgezetPeriode: string | null
}): string {
  if (args.bezwaarOpen) return 'Je bezwaar is in behandeling'
  switch (args.status) {
    case 'NIEUW':
    case 'BESTUURDER_ONBEKEND':
    case 'TE_BEOORDELEN':
      return 'Wordt beoordeeld'
    case 'TE_VERREKENEN':
      return 'Wordt met je salaris verrekend'
    case 'DOORGEZET_SALARIS':
      return args.doorgezetPeriode
        ? `Wordt verrekend met je salaris van ${boetePeriodeLabel(args.doorgezetPeriode)}`
        : 'Wordt met je salaris verrekend'
    case 'DEELS_VERREKEND':
      return 'Deels verrekend met je salaris'
    case 'VERREKEND':
      return 'Verrekend met je salaris'
    case 'NIET_VERHAALD':
    case 'AFGEBOEKT':
      return 'Wordt niet met je salaris verrekend'
  }
}
